import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { resolveMediaUrl, feeUnit } from '../../utils/helpers';
import { reviewsAPI } from '../../services/api';
import WriteReviewModal from '../components/WriteReviewModal';
import AllReviewsModal from '../components/AllReviewsModal';
import {
  Users, GraduationCap, Calendar, MapPin, Phone, Mail,
  Hash, AtSign, Share2, Award, Star, Trophy, Target, Globe,
  Shield, Zap, Clock, ChevronLeft, ChevronRight, CheckCircle, Dumbbell, ZoomIn, X
} from 'lucide-react';

// "06:00" (24h, as stored by Academy Settings) -> "6:00 AM"
function formatHour(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

// ─── Mock data (replace with API in production) ────────────────────────────────

// Highlight content itself is real (academy.highlights, set in Academy Settings) —
// this is just a fixed icon rotation to decorate whatever items exist, cycled by
// position so no icon needs to be stored per-highlight.
const HIGHLIGHT_ICONS = [Award, Users, Target, Shield, Trophy, Zap];

// Level badge colors — same palette ProgramsTab.jsx uses for the real Programs list.
const LEVEL_COLOR_CLASS = {
  beginner: 'bg-green-600',
  intermediate: 'bg-amber-600',
  advanced: 'bg-red-600',
};
const DEFAULT_LEVEL_COLOR_CLASS = 'bg-violet-600';

// "2 weeks ago" / "3 months ago" style relative label for a review's createdAt.
function timeAgo(isoDate) {
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (days < 1) return 'Today';
  if (days === 1) return '1 day ago';
  if (days < 30) return `${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months > 1 ? 's' : ''} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years > 1 ? 's' : ''} ago`;
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function SectionHeading({ title, action, onAction, theme }) {
  return (
    <div className="flex items-end justify-between mb-5">
      <div>
        <h3 className={`text-xl font-bold ${theme.text}`}>{title}</h3>
        <div className={`w-10 h-0.5 rounded mt-1.5 ${theme.tabActive}`} />
      </div>
      {action && (
        <button
          onClick={onAction}
          className={`flex items-center gap-1 text-sm font-semibold ${theme.link} ${theme.linkHover} transition-colors`}
        >
          {action} <ChevronRight size={14} />
        </button>
      )}
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

const OverviewTab = ({ academy, onTabChange }) => {
  const { theme } = useTheme();

  const [reviews, setReviews] = useState([]);
  const [reviewsSummary, setReviewsSummary] = useState({ averageRating: null, totalReviews: 0 });
  // The viewer's own review, when they have already left one. Decides whether
  // the dialog opens blank or holding their words.
  const [myReview, setMyReview] = useState(null);

  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [showWriteReview, setShowWriteReview] = useState(false);
  const [showAllReviews, setShowAllReviews] = useState(false);

  /**
   * The header asks for the write-review dialog; this opens it.
   *
   * The badge beside Share cannot reach this component's state, and the dialog
   * has to live here because the reviews card refreshes from the same
   * submission. An event keeps both of those true without lifting the modal
   * out of the tab that owns it.
   */
  useEffect(() => {
    const open = () => setShowWriteReview(true);
    window.addEventListener('kynaix:write-review', open);
    return () => window.removeEventListener('kynaix:write-review', open);
  }, []);

  // Exposed for the write-review modal to call after a successful submit —
  // no staleness guard needed there (a single, discrete, user-triggered
  // refresh, not a rapid-fire effect that could race against unmount/prop
  // changes the way the mount-time fetch below can).
  const fetchReviews = () => {
    if (!academy?.id) return;
    setReviewsLoading(true);
    reviewsAPI.listForTarget('academy', academy.id, { limit: 6 })
      .then((res) => {
        setReviews(res.data || []);
        setReviewsSummary(res.summary || { averageRating: null, totalReviews: 0 });
        setMyReview(res.myReview || null);
      })
      .catch((err) => console.error('Failed to load academy reviews:', err))
      .finally(() => setReviewsLoading(false));
  };

  useEffect(() => {
    if (!academy?.id) return;
    let cancelled = false;
    setReviewsLoading(true);
    reviewsAPI.listForTarget('academy', academy.id, { limit: 6 })
      .then((res) => {
        if (cancelled) return;
        setReviews(res.data || []);
        setReviewsSummary(res.summary || { averageRating: null, totalReviews: 0 });
        setMyReview(res.myReview || null);
      })
      .catch((err) => {
        if (!cancelled) console.error('Failed to load academy reviews:', err);
      })
      .finally(() => {
        if (!cancelled) setReviewsLoading(false);
      });
    return () => { cancelled = true; };
  }, [academy?.id]);

  // Carousel above About/Contact/Hours — sourced from the main academy fetch
  // (academy.galleryPreview, already capped at 5 images), no separate API call needed.
  const carouselImages = academy.galleryPreview || [];
  const [carouselIndex, setCarouselIndex] = useState(0);
  // Full-screen zoom view — opened by clicking any carousel slide.
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    if (carouselImages.length < 2 || lightboxOpen) return;
    const id = setInterval(() => {
      setCarouselIndex(i => (i + 1) % carouselImages.length);
    }, 5000);
    return () => clearInterval(id);
  }, [carouselImages.length, lightboxOpen]);

  // Guards against a stale index after the gallery shrinks (e.g. owner deletes
  // photos while this page is open elsewhere).
  useEffect(() => {
    if (carouselIndex >= carouselImages.length) setCarouselIndex(0);
  }, [carouselImages.length, carouselIndex]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      if (e.key === 'ArrowLeft') setCarouselIndex(i => (i - 1 + carouselImages.length) % carouselImages.length);
      if (e.key === 'ArrowRight') setCarouselIndex(i => (i + 1) % carouselImages.length);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [lightboxOpen, carouselImages.length]);

  // Real coach profile data (bio, years experience, certifications, per-sport
  // expertise) from academies_staff → users, embedded in the main academy fetch.
  const coaches = (academy.coaches || []).slice(0, 3);

  // Real active programs (same data/query as the Programs tab, capped to 3) —
  // embedded in the main academy fetch so this section doesn't need its own call.
  const featuredPrograms = (academy.programsPreview || []).slice(0, 3);

  const stats = [
    { label: 'Members',        value: academy.memberCount, icon: Users,         bgColorClass: theme.backgroundSecondary,   iconColorClass: theme.link },
    { label: 'Coaches',        value: academy.coachCount,  icon: GraduationCap, bgColorClass: 'bg-green-100',  iconColorClass: 'text-green-600' },
    { label: 'Programs',       value: academy.classCount,  icon: Calendar,      bgColorClass: 'bg-yellow-100', iconColorClass: 'text-yellow-600' }
  ];

  return (
    <div className="space-y-10">

      {/* ── Stats ─────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className={`${theme.card} p-6 rounded-xl flex items-center gap-4`}>
              <div className={`p-3 rounded-xl flex-shrink-0 ${stat.bgColorClass}`}>
                <Icon size={28} className={stat.iconColorClass} />
              </div>
              <div>
                <div className={`text-3xl font-extrabold ${theme.text}`}>{stat.value}</div>
                <div className={`text-sm ${theme.textSecondary}`}>{stat.label}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── About + Gallery preview ───────────────────────────────────────────
           Gallery used to be a full-width autoplaying carousel sitting above
           this row — replaced with a compact thumbnail strip so About isn't
           pushed halfway down the page. Full-screen zoom (lightbox below) is
           unchanged, just now triggered from a thumbnail instead of a slide. */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* About */}
        <div className="lg:col-span-3">
          <div className={`${theme.card} p-6 rounded-xl h-full`}>
            <h4 className={`text-xl font-semibold ${theme.text} mb-3`}>About</h4>
            <p className={`${theme.textSecondary} leading-relaxed mb-5`}>{academy.description}</p>
            <h5 className={`text-base font-semibold ${theme.text} mb-3`}>What We Offer</h5>
            {academy.offerings && academy.offerings.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {academy.offerings.map(offering => (
                  <div key={offering} className="flex items-center gap-2">
                    <CheckCircle size={14} className={`${theme.link} flex-shrink-0`} />
                    <span className={`text-sm ${theme.textSecondary}`}>{offering}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className={`text-sm ${theme.textMuted}`}>No offerings listed yet.</p>
            )}
          </div>
        </div>

        {/* Gallery preview */}
        {carouselImages.length > 0 && (
          <div className={`${theme.card} p-4 rounded-xl lg:col-span-1 flex flex-col`}>
            <div className="flex items-center justify-between mb-3">
              <h5 className={`text-sm font-semibold ${theme.text}`}>Gallery</h5>
              <span className={`text-xs ${theme.textMuted}`}>{carouselImages.length} photo{carouselImages.length === 1 ? '' : 's'}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {carouselImages.slice(0, 4).map((item, i) => (
                <button
                  key={item.id}
                  onClick={() => { setCarouselIndex(i); setLightboxOpen(true); }}
                  className="relative w-full aspect-square rounded-lg overflow-hidden group/thumb"
                >
                  {item.type === 'video' ? (
                    <video src={resolveMediaUrl(item.url)} className="w-full h-full object-cover" muted />
                  ) : (
                    <img
                      src={resolveMediaUrl(item.url)}
                      alt={item.caption || `${academy.name} photo ${i + 1}`}
                      className="w-full h-full object-cover group-hover/thumb:scale-105 transition-transform"
                    />
                  )}
                  {i === 3 && carouselImages.length > 4 && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white font-bold text-sm">
                      +{carouselImages.length - 4}
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover/thumb:bg-black/20 opacity-0 group-hover/thumb:opacity-100 transition-all pointer-events-none">
                    <ZoomIn size={18} className="text-white drop-shadow" />
                  </div>
                </button>
              ))}
            </div>
            {onTabChange && (
              <button
                onClick={() => onTabChange('gallery')}
                className={`mt-3 text-xs font-semibold ${theme.link} ${theme.linkHover} transition-colors text-center`}
              >
                View full gallery →
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Carousel Lightbox — full-screen zoom, opened by clicking a thumbnail ── */}
      {lightboxOpen && carouselImages.length > 0 && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/70 text-sm font-medium">
            {carouselIndex + 1} / {carouselImages.length}
          </div>
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Close"
          >
            <X size={20} className="text-white" />
          </button>
          {carouselImages.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setCarouselIndex(i => (i - 1 + carouselImages.length) % carouselImages.length); }}
              className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
              aria-label="Previous photo"
            >
              <ChevronLeft size={28} className="text-white" />
            </button>
          )}
          <div onClick={(e) => e.stopPropagation()} className="max-w-[90vw] max-h-[85vh] flex flex-col items-center justify-center">
            {carouselImages[carouselIndex].type === 'video' ? (
              <video
                key={carouselImages[carouselIndex].url}
                src={resolveMediaUrl(carouselImages[carouselIndex].url)}
                controls autoPlay
                className="max-w-full max-h-[85vh] rounded-lg shadow-2xl"
              />
            ) : (
              <img
                src={resolveMediaUrl(carouselImages[carouselIndex].url)}
                alt={carouselImages[carouselIndex].caption || `${academy.name} photo ${carouselIndex + 1}`}
                className="max-w-full max-h-[85vh] rounded-lg object-contain shadow-2xl"
              />
            )}
            {carouselImages[carouselIndex].caption && (
              <p className="mt-3 text-sm text-white/80 text-center max-w-lg">{carouselImages[carouselIndex].caption}</p>
            )}
          </div>
          {carouselImages.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); setCarouselIndex(i => (i + 1) % carouselImages.length); }}
              className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
              aria-label="Next photo"
            >
              <ChevronRight size={28} className="text-white" />
            </button>
          )}
        </div>
      )}

      {/* ── Contact & Details ─────────────────────────────────────────────────
           Used to live in a sidebar next to About — moved here as its own row
           now that About's neighboring column is the gallery preview. Ratings
           & Reviews (below) joined this row too. 4 columns fits Contact/Hours/
           Follow Us/Ratings on one row in the common case; Sports Offered only
           shows for academies with 2+ sports and wraps to its own row then. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Contact */}
        <div className={`${theme.card} p-5 rounded-xl`}>
          <h5 className={`text-base font-semibold ${theme.text} mb-3`}>Contact Information</h5>
          <div className="space-y-3">
            {academy.address && (
              <div className="flex items-start gap-3">
                <MapPin size={16} className={`${theme.link} flex-shrink-0 mt-0.5`} />
                <span className={`text-sm ${theme.textSecondary}`}>{academy.address}</span>
              </div>
            )}
            {academy.phone && (
              <div className="flex items-center gap-3">
                <Phone size={16} className={`${theme.link} flex-shrink-0`} />
                <a href={`tel:${academy.phone}`} className={`text-sm ${theme.textSecondary} ${theme.linkHover}`}>{academy.phone}</a>
              </div>
            )}
            {academy.email && (
              <div className="flex items-center gap-3">
                <Mail size={16} className={`${theme.link} flex-shrink-0`} />
                <a href={`mailto:${academy.email}`} className={`text-sm ${theme.textSecondary} ${theme.linkHover} break-all`}>{academy.email}</a>
              </div>
            )}
          </div>
        </div>

        {/* Sports Offered */}
        {academy.sports && academy.sports.length > 1 && (
          <div className={`${theme.card} p-5 rounded-xl`}>
            <h5 className={`text-base font-semibold ${theme.text} mb-3 flex items-center gap-2`}>
              <Dumbbell size={16} className={`${theme.link}`} /> Sports Offered
            </h5>
            <div className="flex flex-wrap gap-2">
              {academy.sports.map(sport => (
                <span key={sport} className={`px-3 py-1 ${theme.backgroundSecondary} ${theme.link} rounded-lg text-sm font-medium`}>
                  {sport}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Ratings & Reviews — compact: latest review only, click through to
            AllReviewsModal for the full paginated list (replaces the old
            full-width Ratings & Reviews section that used to sit near the
            footer). */}
        <div
          id="ratings-reviews"
          onClick={() => setShowAllReviews(true)}
          className={`${theme.card} p-5 rounded-xl cursor-pointer hover:opacity-95 transition-opacity flex flex-col`}
        >
          <div className="flex items-center justify-between mb-3">
            <h5 className={`text-base font-semibold ${theme.text}`}>Ratings & Reviews</h5>
            {reviewsSummary.averageRating != null && (
              <div className="flex items-center gap-1 flex-shrink-0">
                <Star size={14} className="text-yellow-400 fill-yellow-400" />
                <span className={`text-sm font-semibold ${theme.text}`}>{reviewsSummary.averageRating}</span>
                <span className={`text-xs ${theme.textMuted}`}>({reviewsSummary.totalReviews})</span>
              </div>
            )}
          </div>

          {reviewsLoading ? (
            <div className="h-12 animate-pulse rounded bg-black/10" />
          ) : reviews.length === 0 ? (
            <p className={`text-sm ${theme.textMuted} flex-1`}>No reviews yet — be the first to share your experience.</p>
          ) : (
            <div className="flex-1">
              <p className={`text-sm ${theme.textSecondary} line-clamp-2`}>"{reviews[0].reviewText}"</p>
              <div className={`text-xs ${theme.textMuted} mt-1.5`}>
                {[reviews[0].reviewer?.firstName, reviews[0].reviewer?.lastName].filter(Boolean).join(' ') || 'Anonymous'} · {timeAgo(reviews[0].createdAt)}
              </div>
            </div>
          )}

          <button
            onClick={(e) => { e.stopPropagation(); setShowWriteReview(true); }}
            className={`mt-3 text-xs font-semibold ${theme.link} ${theme.linkHover} transition-colors text-left`}
          >
            Write a Review
          </button>
        </div>

        {/* Follow Us */}
        {(academy.instagramLink || academy.facebookLink || academy.twitterLink) && (
          <div className={`${theme.card} p-5 rounded-xl`}>
            <h5 className={`text-base font-semibold ${theme.text} mb-3`}>Follow Us</h5>
            <div className="flex flex-wrap gap-2">
              {academy.instagramLink && (
                <a href={academy.instagramLink} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-opacity hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #833ab4, #fd1d1d, #fcb045)' }}>
                  <Hash size={12} /> Instagram
                </a>
              )}
              {academy.facebookLink && (
                <a href={academy.facebookLink} target="_blank" rel="noopener noreferrer"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold ${theme.tabActive} hover:opacity-90 transition-opacity`}>
                  <Share2 size={12} /> Facebook
                </a>
              )}
              {academy.twitterLink && (
                <a href={academy.twitterLink} target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-gray-800 hover:opacity-90 transition-opacity">
                  <AtSign size={12} /> Twitter / X
                </a>
              )}
            </div>
          </div>
        )}

        {/* Hours */}
        {academy.operatingHours && (academy.operatingHours.weekday || academy.operatingHours.weekend || academy.operatingHours.saturday || academy.operatingHours.sunday) && (
          <div className={`${theme.card} p-5 rounded-xl`}>
            <h5 className={`text-base font-semibold ${theme.text} mb-3`}>Operating Hours</h5>
            <div className="space-y-2 text-sm">
              {[
                { days: 'Mon – Fri', slot: academy.operatingHours.weekday },
                // Saturday/Sunday used to be one combined `weekend` block — academies
                // that haven't been resaved since the per-day split still only have
                // that key, so both days fall back to it.
                { days: 'Saturday',  slot: academy.operatingHours.saturday || academy.operatingHours.weekend },
                { days: 'Sunday',    slot: academy.operatingHours.sunday   || academy.operatingHours.weekend },
              ].filter(({ slot }) => slot).map(({ days, slot }) => (
                <div key={days} className="flex justify-between">
                  <span className={theme.text}>{days}</span>
                  <span className={theme.textSecondary}>
                    {slot.closed ? 'Closed' : `${formatHour(slot.open)} – ${formatHour(slot.close)}`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Highlights ("Why Train With Us" / "Why Join Us" / etc.) ──────────── */}
      {academy.highlights && academy.highlights.length > 0 && (
        <div>
          <SectionHeading title={academy.highlightsHeading || 'Why Choose Us'} theme={theme} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {academy.highlights.map((h, i) => {
              const Icon = HIGHLIGHT_ICONS[i % HIGHLIGHT_ICONS.length];
              return (
                <div key={`${h.title}-${i}`} className={`${theme.card} p-5 rounded-xl flex gap-4 items-start`}>
                  <div className={`p-2.5 rounded-xl flex-shrink-0 ${theme.backgroundSecondary}`}>
                    <Icon size={20} className={`${theme.link}`} />
                  </div>
                  <div>
                    <div className={`font-semibold text-sm ${theme.text} mb-1`}>{h.title}</div>
                    <div className={`text-xs leading-relaxed ${theme.textSecondary}`}>{h.description}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Featured Programs ──────────────────────────────────────────────── */}
      {featuredPrograms.length > 0 && (
        <div>
          <SectionHeading title="Featured Programs" action="View all programs" onAction={() => onTabChange?.('programs')} theme={theme} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {featuredPrograms.map(program => {
              const levelColorClass = LEVEL_COLOR_CLASS[program.level] || DEFAULT_LEVEL_COLOR_CLASS;
              const sessionCount = Array.isArray(program.classes) ? program.classes.length : 0;
              const full = program.maxParticipants != null && program.enrollmentCount >= program.maxParticipants;
              const open = program.registrationOpen && !full;
              return (
                <div key={program.id} className={`${theme.card} rounded-xl overflow-hidden`}>
                  <div className={`relative h-36 ${theme.backgroundSecondary}`}>
                    {program.imageUrl ? (
                      <img src={resolveMediaUrl(program.imageUrl)} alt={program.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Dumbbell size={28} className={`${theme.link}`} />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    {program.level && (
                      <span className={`absolute top-3 left-3 px-2 py-0.5 rounded-full text-xs font-bold text-white capitalize ${levelColorClass}`}>
                        {program.level}
                      </span>
                    )}
                    <span className={`absolute top-3 right-3 px-2 py-0.5 rounded-full text-xs font-bold ${open ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'}`}>
                      {full ? 'Full' : (open ? 'Open' : 'Closed')}
                    </span>
                  </div>
                  <div className="p-4">
                    <h4 className={`font-bold text-sm ${theme.text} mb-2`}>{program.name}</h4>
                    <div className={`space-y-1.5 text-xs ${theme.textSecondary} mb-3`}>
                      {program.timeFrom || program.timeTo ? (
                        <div className="flex items-center gap-2">
                          <Clock size={12} />
                          {program.timeFrom ? formatHour(program.timeFrom) : '—'} – {program.timeTo ? formatHour(program.timeTo) : '—'}
                        </div>
                      ) : sessionCount > 0 && (
                        <div className="flex items-center gap-2"><Clock size={12} /> {sessionCount} {sessionCount === 1 ? 'session' : 'sessions'}</div>
                      )}
                      {(program.sportName || program.ageGroup) && (
                        <div className="flex items-center gap-2"><GraduationCap size={12} /> {[program.sportName, program.ageGroup].filter(Boolean).join(' · ')}</div>
                      )}
                      {program.maxParticipants != null && (
                        <div className="flex items-center gap-2"><Users size={12} /> {program.enrollmentCount}/{program.maxParticipants} enrolled</div>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`font-extrabold text-base ${theme.link}`}>
                        {/* From the price tag, which is what checkout bills.
                          * `fee` was a programme column nothing ever charged. */}
                        {program.price?.amount != null
                          ? <>${program.price.amount}<span className={`text-xs font-normal ${theme.textSecondary}`}>{feeUnit(program)}</span></>
                          : <span className={`text-xs font-normal ${theme.textSecondary}`}>Contact for pricing</span>}
                      </span>
                      <button
                        onClick={() => onTabChange?.('programs')}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold text-white transition-opacity hover:opacity-90 ${open ? `${theme.switchOn} cursor-pointer` : 'bg-gray-400 cursor-not-allowed'}`}
                        disabled={!open}
                      >
                        {full ? 'Waitlist' : (open ? 'Enroll' : 'Closed')}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Meet Our Coaches ───────────────────────────────────────────────── */}
      {coaches.length > 0 && (
        <div>
          <SectionHeading title="Meet Our Coaches" action="View all staff" onAction={() => onTabChange?.('staff')} theme={theme} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {coaches.map(coach => (
              <div key={coach.userId} className={`${theme.card} rounded-xl p-5 flex flex-col items-center text-center`}>
                {coach.profileImage ? (
                  <img src={resolveMediaUrl(coach.profileImage)} alt={coach.name} className="w-20 h-20 rounded-full object-cover mb-3 ring-2 ring-blue-100" />
                ) : (
                  <div className={`w-20 h-20 rounded-full mb-3 ring-2 ${theme.borderAccent} ${theme.backgroundSecondary} flex items-center justify-center ${theme.link} font-bold text-xl`}>
                    {(coach.name || '?').charAt(0)}
                  </div>
                )}
                <div className={`font-bold ${theme.text}`}>{coach.name || 'Coach'}</div>
                {coach.yearsExperience != null && (
                  <div className={`text-xs ${theme.textSecondary} mb-3`}>{coach.yearsExperience} years experience</div>
                )}
                {coach.bio && (
                  <p className={`text-xs ${theme.textSecondary} mb-3`}>{coach.bio}</p>
                )}
                {coach.expertise.length > 0 && (
                  <div className="flex flex-wrap gap-1 justify-center mb-3">
                    {coach.expertise.map(e => (
                      <span key={e.id} className={`px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 ${theme.link}`}>
                        {e.sportName ? `${e.sportName}: ${e.level}` : e.level}
                      </span>
                    ))}
                  </div>
                )}
                {coach.certifications.length > 0 && (
                  <div className="flex flex-wrap gap-1 justify-center">
                    {coach.certifications.map(c => (
                      <span key={c.id} className={`px-2 py-0.5 rounded-full text-xs ${theme.backgroundSecondary} ${theme.textSecondary}`}>{c.name}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <WriteReviewModal
        existingReview={myReview}
        isOpen={showWriteReview}
        onClose={() => setShowWriteReview(false)}
        targetType="academy"
        targetId={academy.id}
        targetName={academy.name}
        onSubmitted={fetchReviews}
      />
      <AllReviewsModal
        isOpen={showAllReviews}
        onClose={() => setShowAllReviews(false)}
        targetType="academy"
        targetId={academy.id}
        targetName={academy.name}
      />

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <div className={`pt-6 border-t ${theme.border} flex flex-col sm:flex-row items-center justify-between gap-3`}>
        <p className={`text-xs ${theme.textMuted}`}>
          {academy.copyrightText || `© ${new Date().getFullYear()} ${academy.name}. All rights reserved.`}
        </p>
        {(academy.instagramLink || academy.facebookLink || academy.twitterLink || academy.website) && (
          <div className="flex items-center gap-3">
            {academy.website && (
              <a href={academy.website} target="_blank" rel="noopener noreferrer" className={`${theme.textMuted} ${theme.linkHover} transition-colors`} title="Website">
                <Globe size={15} />
              </a>
            )}
            {academy.instagramLink && (
              <a href={academy.instagramLink} target="_blank" rel="noopener noreferrer" className={`${theme.textMuted} ${theme.linkHover} transition-colors`} title="Instagram">
                <Hash size={15} />
              </a>
            )}
            {academy.facebookLink && (
              <a href={academy.facebookLink} target="_blank" rel="noopener noreferrer" className={`${theme.textMuted} ${theme.linkHover} transition-colors`} title="Facebook">
                <Share2 size={15} />
              </a>
            )}
            {academy.twitterLink && (
              <a href={academy.twitterLink} target="_blank" rel="noopener noreferrer" className={`${theme.textMuted} ${theme.linkHover} transition-colors`} title="Twitter / X">
                <AtSign size={15} />
              </a>
            )}
          </div>
        )}
      </div>

    </div>
  );
};

export default OverviewTab;
